import React, { useState, useEffect, useCallback } from "react";
import api from "../services/api";

const loadCashfreeScript = () => {
  return new Promise((resolve) => {
    if (window.Cashfree) {
      return resolve(true);
    }
    const existingScript = document.querySelector('script[src*="cashfree.js"]');
    if (existingScript) {
      existingScript.onload = () => resolve(true);
      if (window.Cashfree) return resolve(true);
    }
    const script = document.createElement("script");
    script.src = "https://sdk.cashfree.com/js/v3/cashfree.js";
    script.async = true;
    script.onload = () => {
      console.log("[Subscription] Cashfree SDK script loaded successfully.");
      resolve(true);
    };
    script.onerror = (err) => {
      console.error("[Subscription] Failed to load Cashfree SDK script:", err);
      resolve(false);
    };
    document.body.appendChild(script);
  });
};

function Subscription({ user }) {
  const [plans, setPlans] = useState([]);
  const [currentPlan, setCurrentPlan] = useState(null);
  const [subscription, setSubscription] = useState(null);
  const [usage, setUsage] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sales, setSales] = useState(20000);

  // Selected plan for checkout modal
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [modalError, setModalError] = useState(null);
  const [verifying, setVerifying] = useState(false);
  const [paymentResult, setPaymentResult] = useState(null);
  const [selectedInvoice, setSelectedInvoice] = useState(null);

  const fetchSubscriptionData = useCallback(async () => {
    try {
      setLoading(true);
      const [plansRes, currentRes, historyRes] = await Promise.all([
        api.get("/vendor/subscriptions/plans").catch(() => ({ data: { plans: [] } })),
        api.get("/vendor/subscriptions/current").catch(() => ({ data: {} })),
        api.get("/vendor/subscriptions/history").catch(() => ({ data: { transactions: [] } })),
      ]);

      if (plansRes.data?.plans && plansRes.data.plans.length > 0) {
        setPlans(plansRes.data.plans);
      } else {
        setPlans([
          {
            name: "Basic",
            code: "basic",
            price: 0,
            billingPeriod: "free",
            productLimit: 5,
            commissionRate: 10,
            features: [
              "List up to 5 products",
              "10% marketplace commission",
              "Email seller support",
              "Basic seller portal access",
              "Standard listing visibility",
            ],
            displayOrder: 1,
          },
          {
            name: "Growth",
            code: "growth",
            price: 300,
            billingPeriod: "monthly",
            productLimit: 20,
            commissionRate: 5,
            features: [
              "List up to 20 products",
              "5% marketplace commission",
              "Priority seller support",
              "Full seller portal access",
              "Enhanced listing visibility",
              "Store customization",
            ],
            displayOrder: 2,
          },
          {
            name: "Pro",
            code: "pro",
            price: 499,
            billingPeriod: "monthly",
            productLimit: 50,
            commissionRate: 0,
            features: [
              "List up to 50 products",
              "0% Marketplace Commission",
              "Priority seller support",
              "Full seller portal access",
              "Best listing visibility",
              "Store customization",
              "Early access to new features",
            ],
            displayOrder: 3,
          },
        ]);
      }

      if (currentRes.data?.subscription) {
        setSubscription(currentRes.data.subscription);
        setCurrentPlan(currentRes.data.plan || currentRes.data.subscription.plan);
        setUsage(currentRes.data.usage);
      }
      if (historyRes.data?.transactions) {
        setHistory(historyRes.data.transactions);
      }
    } catch (err) {
      console.error("[Subscription] Failed to load subscription data:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSubscriptionData();
    // Pre-fetch Cashfree SDK script in background
    loadCashfreeScript();
  }, [fetchSubscriptionData]);

  // Handle plan purchase / upgrade click
  const handleInitiatePlan = (plan) => {
    console.log("[Subscription] Plan selected for upgrade:", plan);
    setSelectedPlan(plan);
    setModalError(null);
    setPaymentResult(null);
  };

  /**
   * Cashfree Payment & Subscription Checkout Action
   */
  const handleConfirmPayment = async () => {
    if (!selectedPlan || checkoutLoading) return;

    console.log("[Subscription] Starting payment flow for plan:", selectedPlan.name, selectedPlan.code);
    setCheckoutLoading(true);
    setModalError(null);
    setPaymentResult(null);

    try {
      console.log("[Subscription] Creating payment session via POST /vendor/subscriptions/checkout...");
      const { data } = await api.post("/vendor/subscriptions/checkout", {
        planCode: selectedPlan.code,
        planId: selectedPlan._id || selectedPlan.id,
      });

      console.log("[Subscription] Backend checkout response received:", data);

      // 1. Free Basic Plan activated directly
      if (data.isFreePlan) {
        console.log("[Subscription] Free plan activated directly on server.");
        setPaymentResult({
          success: true,
          planName: selectedPlan.name,
          message: data.msg || `${selectedPlan.name} Plan activated successfully.`,
        });
        setSelectedPlan(null);
        await fetchSubscriptionData();
        return;
      }

      // 2. Paid Plan -> Verify Session ID
      if (!data.paymentSessionId) {
        throw new Error("Cashfree payment session ID was not returned by the server.");
      }

      // 3. Ensure Cashfree SDK is Loaded
      console.log("[Subscription] Ensuring Cashfree SDK is initialized...");
      const isLoaded = await loadCashfreeScript();
      if (!isLoaded || !window.Cashfree) {
        throw new Error(
          "Cashfree payment gateway script could not be loaded. Please check your internet connection or ad-blocker."
        );
      }

      const cfMode = data.mode === "production" ? "production" : "sandbox";
      console.log(`[Subscription] Initializing Cashfree SDK in ${cfMode} mode...`);

      const cashfree = window.Cashfree({
        mode: cfMode,
      });

      // Close pre-checkout modal and enter verification overlay
      setSelectedPlan(null);
      setVerifying(true);

      console.log("[Subscription] Launching cashfree.checkout modal with session:", data.paymentSessionId);

      await cashfree.checkout({
        paymentSessionId: data.paymentSessionId,
        redirectTarget: "_modal",
      });

      console.log("[Subscription] Cashfree checkout modal closed. Verifying payment status on backend...");

      // 4. Server-to-Server Payment Verification
      const statusRes = await api.post("/vendor/subscriptions/payment-status", {
        orderId: data.orderId,
      });

      console.log("[Subscription] Backend verification result:", statusRes.data);

      if (statusRes.data?.isPaid || statusRes.data?.status === "SUCCESS") {
        setPaymentResult({
          success: true,
          planName: selectedPlan.name,
          message: `${selectedPlan.name} Plan is now active!`,
          amount: data.amount,
          orderId: data.orderId,
        });
      } else if (statusRes.data?.status === "USER_DROPPED") {
        setPaymentResult({
          success: false,
          dropped: true,
          message: "Payment was not completed. You can try again whenever you're ready.",
        });
      } else {
        setPaymentResult({
          success: false,
          message: statusRes.data?.msg || "Payment verification was not completed. Your plan has not been activated.",
        });
      }

      await fetchSubscriptionData();
    } catch (error) {
      console.error("[Subscription] Payment initialization failed:", error);
      const rawMsg =
        error.response?.data?.msg ||
        error.response?.data?.error?.message ||
        error.message ||
        "Payment initialization failed. Please try again.";

      setModalError(rawMsg);
      setPaymentResult({
        success: false,
        message: rawMsg,
      });
    } finally {
      setCheckoutLoading(false);
      setVerifying(false);
    }
  };

  const handleViewInvoice = async (transactionId) => {
    try {
      const { data } = await api.get(`/vendor/subscriptions/invoice/${transactionId}`);
      if (data?.invoice) {
        setSelectedInvoice(data.invoice);
      }
    } catch (err) {
      alert("Failed to load invoice details.");
    }
  };

  const currentPlanCode = currentPlan?.code || "basic";
  const basicFee = Math.round(sales * 0.1);
  const growthFee = Math.round(300 + sales * 0.05);
  const proFee = 499;
  const savings = basicFee - proFee;

  if (loading) {
    return (
      <div>
        <div className="skeleton" style={{ height: "32px", width: "220px", marginBottom: "8px" }} />
        <div className="skeleton" style={{ height: "80px", borderRadius: "12px", marginBottom: "24px" }} />
        <div className="row g-4">
          {[1, 2, 3].map((i) => (
            <div key={i} className="col-md-4">
              <div className="skeleton" style={{ height: "420px", borderRadius: "16px" }} />
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="page-header">
        <div className="page-header-left">
          <h2>Seller Subscription Plans</h2>
          <p>Choose the optimal selling tier for your catalog scale and unlock lower marketplace commissions.</p>
        </div>
      </div>

      {/* Verification Overlay */}
      {verifying && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: "rgba(17, 24, 39, 0.8)",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 9999,
            color: "#fff",
            padding: "20px",
          }}
        >
          <div className="spinner-border text-light mb-3" style={{ width: "3.5rem", height: "3.5rem" }} role="status" />
          <h4 style={{ fontWeight: "800", textAlign: "center" }}>Checking your payment with Cashfree...</h4>
          <p style={{ color: "#e5e7eb", fontSize: "14px", textAlign: "center" }}>
            Communicating with the gateway and activating your subscription. Please do not close this window.
          </p>
        </div>
      )}

      {/* Payment Result Notification */}
      {paymentResult && (
        <div
          className={`alert ${
            paymentResult.success
              ? "alert-success"
              : paymentResult.dropped
              ? "alert-warning"
              : "alert-danger"
          } d-flex align-items-center justify-content-between mb-4`}
          style={{ borderRadius: "12px", padding: "16px 20px" }}
        >
          <div>
            <strong>
              {paymentResult.success
                ? "Payment Successful! 🎉"
                : paymentResult.dropped
                ? "Payment Incomplete"
                : "Payment Unsuccessful"}
            </strong>
            <p className="mb-0 mt-1" style={{ fontSize: "14px" }}>
              {paymentResult.message}
            </p>
          </div>
          <button type="button" className="btn-close" onClick={() => setPaymentResult(null)} />
        </div>
      )}

      {/* Current Plan Banner */}
      <div
        className="card-brand"
        style={{
          padding: "20px 24px",
          marginBottom: "28px",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexWrap: "wrap",
          gap: "16px",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: "16px" }}>
          <div
            style={{
              width: "48px",
              height: "48px",
              background: "var(--primary-light)",
              borderRadius: "12px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: "24px",
            }}
          >
            💳
          </div>
          <div>
            <div
              style={{
                fontSize: "11px",
                fontWeight: "700",
                letterSpacing: "0.8px",
                textTransform: "uppercase",
                color: "var(--text-faint)",
              }}
            >
              YOUR CURRENT PLAN
            </div>
            <div style={{ fontSize: "20px", fontWeight: "800", color: "var(--dark)" }}>
              {currentPlan?.name || "Basic"} Plan
            </div>
            <div style={{ fontSize: "13px", color: "var(--text-muted)", marginTop: "2px" }}>
              ₹{currentPlan?.price || 0}/month &middot; {currentPlan?.commissionRate || 10}% Commission &middot; Up to{" "}
              {currentPlan?.productLimit || 5} Products
            </div>
          </div>
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: "16px" }}>
          {subscription?.expiresAt && (
            <div style={{ textAlign: "right" }}>
              <div style={{ fontSize: "11px", color: "var(--text-faint)", fontWeight: "700" }}>NEXT BILLING</div>
              <div style={{ fontSize: "13px", fontWeight: "700", color: "var(--dark)" }}>
                {new Date(subscription.expiresAt).toLocaleDateString("en-IN", {
                  day: "numeric",
                  month: "short",
                  year: "numeric",
                })}
              </div>
            </div>
          )}
          <span className={`status-pill ${subscription?.status === "active" ? "active" : "pending"}`}>
            <span className="status-dot" />
            {subscription?.status === "active" ? "Active" : subscription?.status || "Active"}
          </span>
        </div>
      </div>

      {/* Product Capacity Progress */}
      {usage && (
        <div
          style={{
            background: "#fff",
            border: "1px solid #e5e7eb",
            borderRadius: "12px",
            padding: "16px 20px",
            marginBottom: "32px",
          }}
        >
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "8px" }}>
            <span style={{ fontSize: "13px", fontWeight: "700", color: "var(--dark)" }}>
              Product Capacity: {usage.productCount} / {usage.productLimit} Products Listed
            </span>
            <span
              style={{
                fontSize: "12px",
                fontWeight: "700",
                color: usage.isAtLimit ? "var(--brand-primary)" : "var(--text-muted)",
              }}
            >
              {usage.percentUsed}% Utilized
            </span>
          </div>
          <div style={{ height: "8px", background: "#f3f4f6", borderRadius: "99px", overflow: "hidden" }}>
            <div
              style={{
                width: `${usage.percentUsed}%`,
                height: "100%",
                background: usage.isAtLimit ? "var(--brand-primary)" : "#10b981",
                borderRadius: "99px",
                transition: "width 0.3s",
              }}
            />
          </div>
        </div>
      )}

      {/* Plan Cards */}
      <div className="row g-4 mb-5" style={{ paddingTop: "8px" }}>
        {plans.map((plan) => {
          const isCurrent = currentPlanCode === plan.code;
          const isFeatured = plan.code === "growth";

          return (
            <div key={plan.code} className="col-md-4">
              <div
                className={"plan-card" + (isFeatured ? " featured" : "")}
                style={{
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "space-between",
                }}
              >
                <div>
                  {isFeatured && <div className="plan-badge-top">MOST POPULAR</div>}
                  <h4
                    style={{
                      fontWeight: "800",
                      color: "var(--dark)",
                      marginBottom: "6px",
                      marginTop: isFeatured ? "8px" : "0",
                    }}
                  >
                    {plan.name}
                  </h4>
                  <div style={{ marginBottom: "16px" }}>
                    <span className="plan-price">{plan.price === 0 ? "Free" : `₹${plan.price}`}</span>
                    {plan.billingPeriod === "monthly" && <span className="plan-price-period">/month</span>}
                  </div>

                  <div
                    style={{
                      padding: "8px 12px",
                      background: "#f9fafb",
                      borderRadius: "8px",
                      marginBottom: "16px",
                      fontSize: "12px",
                      color: "var(--text-mid)",
                      fontWeight: "600",
                    }}
                  >
                    🚀 {plan.productLimit} Products &middot; {plan.commissionRate}% Commission
                  </div>

                  <ul className="plan-features">
                    {(plan.features || []).map((f, i) => (
                      <li key={i}>
                        <span className="plan-check">✓</span>
                        {f}
                      </li>
                    ))}
                  </ul>
                </div>

                <div style={{ marginTop: "24px" }}>
                  {isCurrent ? (
                    <button className="btn btn-outline-secondary w-100" disabled style={{ padding: "11px", fontWeight: "700" }}>
                      ✓ Current Plan
                    </button>
                  ) : (
                    <button
                      onClick={() => handleInitiatePlan(plan)}
                      className={`btn ${isFeatured ? "btn-primary" : "btn-outline-primary"} w-100`}
                      style={{ padding: "11px", fontWeight: "700" }}
                    >
                      {plan.price === 0 ? "Switch to Basic" : `Upgrade to ${plan.name}`}
                    </button>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Interactive Savings Calculator */}
      <div className="calculator-card mb-5">
        <h4 style={{ fontWeight: "800", textAlign: "center", color: "var(--dark)", marginBottom: "6px" }}>
          Calculate Your Monthly Savings
        </h4>
        <p style={{ textAlign: "center", color: "var(--text-muted)", fontSize: "14px", marginBottom: "28px" }}>
          Compare commission fees across plans based on your monthly sales turnover
        </p>

        <div className="row g-5 align-items-center">
          <div className="col-md-6">
            <label className="form-label" style={{ fontWeight: "700", color: "var(--dark)" }}>
              Expected Monthly Sales
            </label>
            <input
              type="range"
              className="form-range"
              min="5000"
              max="150000"
              step="5000"
              value={sales}
              onChange={(e) => setSales(Number(e.target.value))}
            />
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                marginTop: "8px",
                fontSize: "13px",
                fontWeight: "600",
                color: "var(--text-muted)",
              }}
            >
              <span>₹5,000</span>
              <span style={{ fontSize: "20px", fontWeight: "900", color: "var(--brand-primary)" }}>
                ₹{sales.toLocaleString("en-IN")}
              </span>
              <span>₹1,50,000</span>
            </div>
          </div>

          <div className="col-md-6">
            <h6 style={{ fontWeight: "700", color: "var(--text-mid)", marginBottom: "16px" }}>
              Estimated Monthly Total Cost
            </h6>
            <div className="calc-fee-row">
              <span className="calc-fee-plan">Basic Plan (10% Commission)</span>
              <span className="calc-fee-amount">₹{basicFee.toLocaleString("en-IN")}</span>
            </div>
            <div className="calc-fee-row">
              <span className="calc-fee-plan">Growth Plan (₹300 + 5% Commission)</span>
              <span className="calc-fee-amount">₹{growthFee.toLocaleString("en-IN")}</span>
            </div>
            <div className="calc-fee-row best-value">
              <span className="calc-fee-plan">Pro Plan (₹499 + 0% Commission)</span>
              <span className="calc-fee-amount">₹{proFee.toLocaleString("en-IN")}</span>
            </div>
            {savings > 0 && (
              <div className="calc-savings-badge" style={{ marginTop: "12px" }}>
                💰 Save ₹{savings.toLocaleString("en-IN")} / month with Pro Plan vs Basic
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Subscription Payment History */}
      <div className="card-brand" style={{ padding: "24px" }}>
        <h4 style={{ fontWeight: "800", color: "var(--dark)", marginBottom: "16px" }}>
          Subscription &amp; Payment History
        </h4>

        {history.length === 0 ? (
          <div style={{ textAlign: "center", padding: "32px 16px", color: "var(--text-muted)" }}>
            <p className="mb-0">No past payment transactions recorded yet.</p>
          </div>
        ) : (
          <div className="table-responsive">
            <table className="table align-middle">
              <thead>
                <tr style={{ fontSize: "12px", textTransform: "uppercase", color: "var(--text-faint)" }}>
                  <th>Date</th>
                  <th>Plan</th>
                  <th>Amount</th>
                  <th>Order Reference</th>
                  <th>Status</th>
                  <th style={{ textAlign: "right" }}>Receipt</th>
                </tr>
              </thead>
              <tbody>
                {history.map((tx) => (
                  <tr key={tx._id || tx.orderId}>
                    <td style={{ fontSize: "13px", color: "var(--text-mid)" }}>
                      {new Date(tx.paidAt || tx.createdAt).toLocaleDateString("en-IN", {
                        day: "numeric",
                        month: "short",
                        year: "numeric",
                      })}
                    </td>
                    <td style={{ fontWeight: "700", color: "var(--dark)" }}>{tx.planName}</td>
                    <td style={{ fontWeight: "700" }}>₹{tx.amount}</td>
                    <td style={{ fontSize: "12px", fontFamily: "monospace", color: "var(--text-muted)" }}>
                      {tx.orderId}
                    </td>
                    <td>
                      <span className={`status-pill ${tx.status === "SUCCESS" ? "active" : "pending"}`}>
                        <span className="status-dot" />
                        {tx.status}
                      </span>
                    </td>
                    <td style={{ textAlign: "right" }}>
                      <button
                        className="btn btn-sm btn-outline-secondary"
                        onClick={() => handleViewInvoice(tx._id || tx.orderId)}
                      >
                        View Receipt
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Pre-Payment Confirmation Modal */}
      {selectedPlan && (
        <div className="modal show d-block" tabIndex="-1" style={{ background: "rgba(0,0,0,0.6)" }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content" style={{ borderRadius: "16px", overflow: "hidden" }}>
              <div className="modal-header" style={{ borderBottom: "1px solid #f3f4f6", padding: "18px 24px" }}>
                <h5 className="modal-title" style={{ fontWeight: "800", color: "var(--dark)" }}>
                  Confirm Subscription Plan
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => {
                    setSelectedPlan(null);
                    setModalError(null);
                  }}
                />
              </div>

              <div className="modal-body" style={{ padding: "24px" }}>
                {modalError && (
                  <div
                    className="alert alert-danger d-flex align-items-center mb-3"
                    style={{ borderRadius: "10px", fontSize: "13px", padding: "12px 16px" }}
                  >
                    <div>
                      <strong>Unable to start payment: </strong>
                      <span>{modalError}</span>
                    </div>
                  </div>
                )}

                <div style={{ textAlign: "center", marginBottom: "20px" }}>
                  <div
                    style={{
                      fontSize: "13px",
                      fontWeight: "700",
                      color: "var(--text-faint)",
                      textTransform: "uppercase",
                      letterSpacing: "0.5px",
                    }}
                  >
                    Selected Plan
                  </div>
                  <h3 style={{ fontWeight: "900", color: "var(--dark)", marginTop: "4px" }}>
                    {selectedPlan.name} Plan
                  </h3>
                  <div style={{ fontSize: "26px", fontWeight: "900", color: "var(--brand-primary)" }}>
                    {selectedPlan.price === 0 ? "Free" : `₹${selectedPlan.price} / month`}
                  </div>
                  <div style={{ fontSize: "12px", color: "var(--text-muted)" }}>
                    {selectedPlan.billingPeriod === "free" ? "Unlimited Free" : "Billed monthly"}
                  </div>
                </div>

                <div style={{ background: "#f9fafb", borderRadius: "12px", padding: "16px", marginBottom: "20px" }}>
                  <div style={{ fontWeight: "700", fontSize: "13px", color: "var(--dark)", marginBottom: "8px" }}>
                    Includes:
                  </div>
                  <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: "8px", fontSize: "13px" }}>
                      <span style={{ color: "#16a34a", fontWeight: "bold" }}>✓</span>
                      <span>Up to {selectedPlan.productLimit} products</span>
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: "8px", fontSize: "13px" }}>
                      <span style={{ color: "#16a34a", fontWeight: "bold" }}>✓</span>
                      <span>{selectedPlan.commissionRate}% marketplace commission</span>
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: "8px", fontSize: "13px" }}>
                      <span style={{ color: "#16a34a", fontWeight: "bold" }}>✓</span>
                      <span>Priority seller tools</span>
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: "8px", fontSize: "13px" }}>
                      <span style={{ color: "#16a34a", fontWeight: "bold" }}>✓</span>
                      <span>Marketplace listing &amp; customer discovery</span>
                    </div>
                  </div>
                </div>

                <div
                  style={{
                    borderTop: "1px dashed #e5e7eb",
                    paddingTop: "14px",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <span style={{ fontWeight: "700", color: "var(--dark)" }}>Total Payable Now</span>
                  <span style={{ fontSize: "20px", fontWeight: "900", color: "var(--dark)" }}>
                    {selectedPlan.price === 0 ? "₹0 (Free)" : `₹${selectedPlan.price}`}
                  </span>
                </div>
              </div>

              <div className="modal-footer" style={{ borderTop: "1px solid #f3f4f6", padding: "16px 24px" }}>
                <button
                  type="button"
                  className="btn btn-outline-secondary"
                  onClick={() => {
                    setSelectedPlan(null);
                    setModalError(null);
                  }}
                  disabled={checkoutLoading}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={handleConfirmPayment}
                  disabled={checkoutLoading}
                  style={{
                    minWidth: "190px",
                    fontWeight: "700",
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: "8px",
                  }}
                >
                  {checkoutLoading ? (
                    <>
                      <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
                      Creating secure payment...
                    </>
                  ) : selectedPlan.price === 0 ? (
                    "Activate Free Plan"
                  ) : (
                    "Continue to Payment"
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Invoice / Receipt Modal */}
      {selectedInvoice && (
        <div className="modal show d-block" tabIndex="-1" style={{ background: "rgba(0,0,0,0.6)" }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content" style={{ borderRadius: "16px" }}>
              <div className="modal-header" style={{ borderBottom: "1px solid #f3f4f6", padding: "18px 24px" }}>
                <h5 className="modal-title" style={{ fontWeight: "800" }}>Subscription Invoice Receipt</h5>
                <button type="button" className="btn-close" onClick={() => setSelectedInvoice(null)} />
              </div>

              <div className="modal-body" style={{ padding: "24px" }}>
                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "16px" }}>
                  <div>
                    <div style={{ fontSize: "11px", fontWeight: "700", color: "var(--text-faint)" }}>INVOICE NO</div>
                    <div style={{ fontWeight: "800", color: "var(--dark)" }}>{selectedInvoice.invoiceNumber}</div>
                  </div>
                  <div style={{ textAlign: "right" }}>
                    <div style={{ fontSize: "11px", fontWeight: "700", color: "var(--text-faint)" }}>DATE</div>
                    <div style={{ fontWeight: "700" }}>{new Date(selectedInvoice.date).toLocaleDateString("en-IN")}</div>
                  </div>
                </div>

                <div style={{ background: "#f9fafb", borderRadius: "10px", padding: "14px", marginBottom: "16px" }}>
                  <div style={{ fontSize: "12px", color: "var(--text-muted)" }}>Billed To:</div>
                  <div style={{ fontWeight: "700", color: "var(--dark)" }}>{selectedInvoice.vendor?.businessName}</div>
                  <div style={{ fontSize: "12px", color: "var(--text-mid)" }}>
                    {selectedInvoice.vendor?.sellerName} ({selectedInvoice.vendor?.email})
                  </div>
                </div>

                <div style={{ borderTop: "1px solid #e5e7eb", borderBottom: "1px solid #e5e7eb", padding: "12px 0", marginBottom: "16px" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", fontWeight: "700", marginBottom: "4px" }}>
                    <span>{selectedInvoice.planName} Plan ({selectedInvoice.billingPeriod})</span>
                    <span>₹{selectedInvoice.amount}</span>
                  </div>
                  <div style={{ display: "flex", justifyContent: "space-between", fontSize: "12px", color: "var(--text-muted)" }}>
                    <span>Subtotal</span>
                    <span>₹{selectedInvoice.subtotal}</span>
                  </div>
                  <div style={{ display: "flex", justifyContent: "space-between", fontSize: "12px", color: "var(--text-muted)" }}>
                    <span>GST ({selectedInvoice.gstRate})</span>
                    <span>₹{selectedInvoice.gst}</span>
                  </div>
                </div>

                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", fontWeight: "800", fontSize: "16px" }}>
                  <span>Total Amount Paid</span>
                  <span style={{ color: "var(--brand-primary)" }}>₹{selectedInvoice.amount}</span>
                </div>
              </div>

              <div className="modal-footer" style={{ borderTop: "1px solid #f3f4f6", padding: "14px 24px" }}>
                <button type="button" className="btn btn-secondary w-100" onClick={() => setSelectedInvoice(null)}>
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Subscription;
